import React, { useEffect, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import { format } from 'date-fns';
import { addStatusToast, Loader, SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getSeasonRatesAsync,
  saveSeasonRateAsync,
  SeasonRateDetails,
} from './seasonRatesSlice';
import { SEASON_RATE_COLORS, SEASON_RATE_TYPE } from '../../common/constants';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';

function AddSeasonRate() {
  const location = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const parsedQuery = queryString.parse(location.search.replace('?', ''));
  const inputErrors = useAppSelector(selectInputErrors);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accommodationId, setAccommodationId] = useState<string>('');
  const [selectedAccommodationName, setSelectedAccommodationName] =
    useState<string>('');

  const initialSeasonRateState: SeasonRateDetails = {
    accommodationId: params.accommodationId,
    seasonName: '',
    startDate: null,
    endDate: null,
    priceForAdditionalDay: 0,
    dailyPurchasePrice: 0,
    commissionPercent: 0,
    checkInTime: '',
    checkOutTime: '',
    restrictedCheckInWeekdays: [],
    restrictedCheckOutWeekdays: [],
    ratePrices: [
      { duration: 1, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 2, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 3, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 4, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 5, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 6, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 7, dailyPrice: 0, dailyPurchasePrice: 0 },
    ],
  };

  const [seasonRate, setSeasonRate] = useState(initialSeasonRateState);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (params.accommodationId && parsedQuery.accommodationId !== '') {
      setAccommodationId(params.accommodationId);
    }

    if (parsedQuery.accommodation && parsedQuery.accommodation !== '') {
      setSelectedAccommodationName(
        decodeURI(parsedQuery.accommodation.toString()),
      );
    }
  }, [params, parsedQuery.accommodation, parsedQuery.accommodationId]);

  useEffect(() => {
    if (accommodationId !== '' && selectedAccommodationName !== '') {
      setIsLoading(false);
    }
  }, [accommodationId, selectedAccommodationName]);

  useEffect(() => {
    setSeasonRate(initialSeasonRateState);
    setSubmitted(false);
  }, [accommodationId]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newSeasonRate = { ...seasonRate };
    let pointer = newSeasonRate;

    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });

    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setSeasonRate(newSeasonRate);
  };

  const handleNumericInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const path = name.split('.');
    const finalProp = path.pop();
    const newSeasonRate = { ...seasonRate };
    let pointer = newSeasonRate;

    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });

    pointer[finalProp] = value !== '' ? value : null;
    setSeasonRate(newSeasonRate);
  };

  const handleSelectChange = (selectedOption, name) => {
    const path = name.split('.');
    const finalProp = path.pop();
    const newSeasonRate = { ...seasonRate };
    let pointer = newSeasonRate;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] = selectedOption.value;
    setSeasonRate(newSeasonRate);
  };

  const handleDateChange = (field: string, value: string | Date) => {
    setSeasonRate(prevState => ({
      ...prevState,
      [field]: value instanceof Date ? format(value, 'yyyy-MM-dd') : value,
    }));
  };

  const handleRatePrices = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { duration } = event.target.dataset;
    const textValue = event.target.value;
    const newSeasonRate = { ...seasonRate };

    newSeasonRate[finalProp] = seasonRate[finalProp].map(item => {
      if (item.duration.toString() === duration.toString()) {
        return { ...item, dailyPrice: textValue !== '' ? textValue : 0 };
      }
      return item;
    });
    setSeasonRate(newSeasonRate);
  };

  const onSubmit = async () => {
    const actionResult = await dispatch(saveSeasonRateAsync(seasonRate));
    if (saveSeasonRateAsync.fulfilled.match(actionResult)) {
      dispatch(
        addStatusToast({
          Message: `Tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
      dispatch(
        getSeasonRatesAsync({ accommodationId: params.accommodationId }),
      );
    }
  };

  const seasonRateTypeOptions = [
    { value: SEASON_RATE_TYPE.Normal, label: 'Normaali sesonki' },
    { value: SEASON_RATE_TYPE.Special, label: 'Tapahtuma tms.' },
  ];

  const renderRatePriceItems = seasonRate.ratePrices?.map((item, index) => (
    <Form.Group className="mb-2" key={item.duration}>
      <Form.Label>{item.duration} vrk</Form.Label>
      <Form.Control
        type="text"
        id="ratePrices"
        name="ratePrices"
        data-duration={item.duration}
        onChange={handleRatePrices}
        value={item.dailyPrice || ''}
      />
      <Form.Text className="text-muted">
        Ostohinta: {item.dailyPurchasePrice}
      </Form.Text>
    </Form.Group>
  ));

  const seasonRateColorOptions = SEASON_RATE_COLORS.map(color => {
    return {
      value: color.id,
      label: color.name,
    };
  });

  return (
    <>
      <Helmet>
        <title>
          Hinnoittelu - Lisää sesonkihintakausi - {selectedAccommodationName}
        </title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink="/rates/seasonrates"
          Breadcrumbs={['Hinnoittelu', 'Sesonkihintakaudet']}
          Title={`Uusi sesonkihintakausi: ${selectedAccommodationName}`}
        />
      </Container>

      <Container className="container-form">
        <Loader IsLoading={isLoading} />
        {!isLoading && (
          <>
            <Form.Group className="my-5">
              <h4 className="text-uppercase text-muted mb-2">
                Hintakauden perustiedot
              </h4>
              <Form.Label>Hintakauden tyyppi</Form.Label>
              <Select
                inputId="seasonRateType"
                name="seasonRateType"
                placeholder="Valitse hintakauden tyyppi"
                isClearable
                options={seasonRateTypeOptions}
                onChange={option =>
                  handleSelectChange(option, 'seasonRateType')
                }
                value={seasonRateTypeOptions.filter(
                  option => option.value === seasonRate.seasonRateType,
                )}
              />
              <Form.Label>Hintakauden nimi</Form.Label>
              <Form.Control
                type="text"
                id="seasonName"
                name="seasonName"
                placeholder="Anna hintakauden nimi"
                onChange={handleInputChange}
                value={seasonRate.seasonName}
                required
              />
              <Form.Label>Hintakausi alkaa</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker
                  locale={fi}
                  id="startDate"
                  name="startDate"
                  dateFormat="dd.MM.yyyy"
                  selected={
                    seasonRate?.startDate
                      ? new Date(seasonRate.startDate)
                      : null
                  }
                  onChange={date => handleDateChange('startDate', date)}
                  className="form-control"
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Label>Hintakausi päättyy</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker
                  locale={fi}
                  id="endDate"
                  name="endDate"
                  dateFormat="dd.MM.yyyy"
                  selected={
                    seasonRate?.endDate ? new Date(seasonRate.endDate) : null
                  }
                  onChange={date => handleDateChange('endDate', date)}
                  className="form-control"
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
            </Form.Group>
            {seasonRate.seasonRateType === SEASON_RATE_TYPE.Normal && (
              <Form.Group className="mb-24">
                <Form.Label>Hintakauden väri</Form.Label>
                <Select
                  inputId="seasonRateColorId"
                  name="seasonRateColorId"
                  placeholder="Valitse väri..."
                  isClearable
                  options={seasonRateColorOptions}
                  onChange={option =>
                    handleSelectChange(option, 'seasonRateColorId')
                  }
                  value={seasonRateColorOptions.filter(
                    option => option.value === seasonRate.seasonRateColorId,
                  )}
                  classNames={{
                    control: state => 'form-control form-control-react-select',
                  }}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-5">
              <h4 className="text-uppercase text-muted mb-2">
                Sisään- ja uloskirjautuminen
              </h4>
              <Form.Label>Sisäänkirjautumisen kellonaika</Form.Label>
              <Form.Control
                type="text"
                id="checkInTime"
                name="checkInTime"
                placeholder="Anna sisäänkirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                onChange={handleInputChange}
                value={seasonRate.checkInTime}
                required
              />
              <Form.Label>Uloskirjautumisen kellonaika</Form.Label>
              <Form.Control
                type="text"
                id="checkOutTime"
                name="checkOutTime"
                placeholder="Anna uloskirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                onChange={handleInputChange}
                value={seasonRate.checkOutTime}
                required
              />
            </Form.Group>

            <Form.Group className="mb-5">
              <h4 className="text-uppercase text-muted mb-2">
                Hintakauden hinnoittelu
              </h4>
              <Form.Label>Provisioprosentti</Form.Label>
              <Form.Control
                type="text"
                id="commissionPercent"
                name="commissionPercent"
                placeholder="Anna provisioprosentti"
                onChange={handleNumericInput}
                value={seasonRate.commissionPercent || ''}
                required
              />
            </Form.Group>
            <h4 className="text-uppercase text-muted mb-2">
              Hinnat keston mukaan
            </h4>
            {renderRatePriceItems}
            <Form.Label>Lisävuorokauden hinta</Form.Label>
            <Form.Control
              type="text"
              id="priceForAdditionalDay"
              name="priceForAdditionalDay"
              placeholder="Lisävuorokauden hinta"
              onChange={handleNumericInput}
              value={seasonRate.priceForAdditionalDay || ''}
              required
            />
            <Form.Text className="text-muted">
              Käytetään päivähintana yli 7 vuorokauden varauksiin. Hinta
              lisätään jokaiselle vuorokaudelle joka ylittää 7 vuorokautta. Esim
              10 vuorokauden varauksessa hinta on 7 vuorokauden hinta + 3 x
              lisävuorokauden hinta.
            </Form.Text>

            <InputErrorsDisplay inputErrors={inputErrors} />

            <div className="border-top mt-5 pt-5 mb-5 pb-5">
              <Button className="w-100" onClick={onSubmit}>
                Tallenna hintakausi
              </Button>
            </div>
          </>
        )}
      </Container>
    </>
  );
}

export default AddSeasonRate;
